import React, { useEffect, useMemo, useState } from 'react';

import { useGameDetails } from 'utils/Hooks';
import TableItem from './table_item';
import { telegramGroupHyperlink, unixTimestampHyperlink } from 'utils/Misc';
import '../styles.scss';
import './historic_feed.scss';

const BINANCE_HISTORIC_FEED = 'https://api.binance.com/api/v3/klines?symbol=';
const SECONDS_OF_HISTORY_TO_LOOKUP = 2;

const FEED_REQUEST_OPTIONS = {
  method: 'GET',
  //mode: 'no-cors', // only results in usable data for geo-fenced IPs, threfore it's unused
  headers: {
    //'Content-Type': 'application/json',
  },
};


function HistoricPriceFeedData(props) {
  const gameDetails = useGameDetails();

  const [allHistPriceData, SetAllHistPriceData] = useState(undefined);
  const [wantedHistDataIndex, SetWantedHistDataIndex] = useState(SECONDS_OF_HISTORY_TO_LOOKUP);
  const [isSessionGeoFenced, SetSessionGeoFenced] = useState(false);

  const feedURL = useMemo(GetComposedFeedURL, [gameDetails, props.round, props.wantedtimestamp]);

  useEffect(() => {
    UpdateHistoricPriceData();
  }, [gameDetails, feedURL, props.wantedTimestamp]);


  async function UpdateHistoricPriceData() {
    const rawHistPriceData = await FetchHistoricPriceData();
    if (!rawHistPriceData) {
      SetAllHistPriceData(null);
      return;
    }
    const parsedPriceData = [];
    for (const item of rawHistPriceData) {
      const parsedItem = {
        'openTime': item[0],
        'open': Number(item[1]).toFixed(gameDetails.priceDecimals),
        'high': Number(item[2]).toFixed(gameDetails.priceDecimals),
        'low': Number(item[3]).toFixed(gameDetails.priceDecimals),
        'close': Number(item[4]).toFixed(gameDetails.priceDecimals),
        'volume': item[5],
        'closeTime': item[6],
        'quoteAssetVolume': item[7],
        'nOfTrades': item[8],
        'takerBuyBaseAssetVolume': item[9],
        'takerBuyQuoteAssetVolume': item[10],
        'unknown': item[11]
      }
      parsedPriceData.push(parsedItem);
    }
    SetAllHistPriceData(parsedPriceData);
  }

  function GetComposedFeedURL() {
    if (!props.wantedTimestamp)
      return null;
    const symbol = gameDetails.tickerSymbol + 'USDT';
    const startSeconds = Number(props.wantedTimestamp) - SECONDS_OF_HISTORY_TO_LOOKUP;
    const startMiliseconds = startSeconds * 1000;
    const endSeconds = Number(props.wantedTimestamp) + SECONDS_OF_HISTORY_TO_LOOKUP;
    const endMiliseconds = endSeconds * 1000;
    const interval = '1s';
    const url = BINANCE_HISTORIC_FEED +
      symbol + '&interval=' + interval + '&limit=10' +
      '&startTime=' + startMiliseconds +
      '&endTime=' + endMiliseconds;
    return url;
  }

  async function FetchHistoricPriceData() {
    if (!feedURL) return;
    let response = null;
    try {
      response = await fetch(feedURL, FEED_REQUEST_OPTIONS);
    } catch (error) {
      if (String(error.message).includes('Failed to fetch'))
        SetSessionGeoFenced(true);
      return null;
    }
    if (response?.ok) {
      const data = await response?.json();
      if (!data)
        return null
      else if (data && JSON.stringify(data).includes('restricted location'))
        SetSessionGeoFenced(true);
      return data;
    } else {
      return null;
    }
  }

  function UsePreviousSecondOfPriceData() {
    const nextHistDataIndex = wantedHistDataIndex - 1;
    if (nextHistDataIndex < 0)
      return;
    SetWantedHistDataIndex(nextHistDataIndex);
  }
  function UseNextSecondOfPriceData() {
    const nextHistDataIndex = wantedHistDataIndex + 1;
    if (nextHistDataIndex >= allHistPriceData.length)
      return;
    SetWantedHistDataIndex(nextHistDataIndex);
  }

  function IsWantedTimestampBeingViewed() {
    if (!allHistPriceData || !allHistPriceData[wantedHistDataIndex]?.openTime) return false;
    const timestampBeingViewed = (allHistPriceData[wantedHistDataIndex].openTime / 1000)?.toFixed(0);
    if (timestampBeingViewed == props.wantedTimestamp)
      return true;
    else
      return false;
  }


  const geoFencedNotice = (<div className='GeoFenceNotice'>
    <p>Failed to fetch historic data. Likely geo-fenced by Binance.com.</p>
    Starting with 2023 - Binance.com doesn't provide price feed data to some countries, such as The USA and Iran.<br />
    Below are some ways to work around this:
    <p>1. Use a VPN/proxy with a European IP address.</p>
    <p>2. Ask someone in our {telegramGroupHyperlink} to verify the price for you.</p>
  </div>);

  const introductionM = useMemo(Introduction, [gameDetails, isSessionGeoFenced, allHistPriceData]) // eslint-disable-line react-hooks/exhaustive-deps
  function Introduction() {
    if (isSessionGeoFenced || !allHistPriceData)
      return null;
    return (<details>
      <summary>What is this?</summary>
      <div className='Indented'>
        This allows for you to verify the price stored in our contract against {' '}
        historic Binance.com price data. Below you'll see values pulled directly from{' '}
        historic price feed of Binance. They show price movements of {gameDetails.tickerSymbol}{' '}
        within <b>a single second</b>.
        <ul>
          <li><span className='ListSpacingFix'>
            <i>"Opening price"</i> is the price at the start
            of the second, <i>"Closing price"</i> is the price at the end of the second.
          </span></li>
          <li><span className='ListSpacingFix'>
            <i>"Lowest price"</i> and <i>"Highest price"</i> show the highest and lowest price{' '}
            of {gameDetails.tickerSymbol} within a single second. The price you're verifying{' '}
            should be within that range.
          </span></li>
          <li><span className='ListSpacingFix'>
            <i>"Observed at"</i> are the times at which opening and closing prices{' '}
            were observed on Binance, according to their own price feed data.{' '}
            <span className='Thin'>Those times are in Unix{' '}
              millisecond timestamp formats, and can be converted to human-readable date & time{' '}
              at {unixTimestampHyperlink}
            </span>{' '}
          </span></li>
        </ul>
      </div>
    </details>);
  }


  const summaryM = useMemo(Summary, [wantedHistDataIndex, allHistPriceData, isSessionGeoFenced,
    gameDetails, props.wantedPrice, props.wantedTimestamp]);
  function Summary() {
    if (!IsWantedTimestampBeingViewed() || props.wantedPrice === null)
      return null;
    else if (isSessionGeoFenced)
      return (<p>
        Geo-fenced by Binance with the following message: <span>{allHistPriceData.msg}</span><br />
        Binance.com began restricting access from various countires in the Autumn of 2022.{' '}
        Your best option is to use a European VPN/proxy and try again, as European addresses{' '}
        are known to work.
      </p>);
    else if (!allHistPriceData)
      return (<p>Failed to fetch historic data;{' '}
        try reloading this page our contact us in our {telegramGroupHyperlink}</p>);
    let message = null;
    const histData = allHistPriceData[wantedHistDataIndex];
    const wantedPrice = (Number(props.wantedPrice) / 10 ** 8).toFixed(gameDetails.priceDecimals);
    const priceWithinRange = (histData.low <= wantedPrice && wantedPrice <= histData.high);
    if (priceWithinRange)
      message = (<p>
        <span className='BullGreen'>Price is correct</span>{' '}
        and is within range
      </p>);
    else
      message = (<p>
        <span className='BearRed'>Price doesn't match</span>;{' '}
        this round might be eligible for refund
      </p>)
    return (<div className='SummaryOfHistoricData'>
      {message}
      <Range />
    </div >);
  }

  function Range() {
    let firstSign = '≤', secondSign = '≤';
    const histData = allHistPriceData[wantedHistDataIndex];
    const wantedPrice = (Number(props.wantedPrice) / 10 ** 8).toFixed(gameDetails.priceDecimals);
    if (wantedPrice < histData.low)
      firstSign = <span className='BearRed'>{firstSign}</span>
    if (wantedPrice > histData.high)
      secondSign = <span className='BearRed'>{secondSign}</span>
    return (<p>
      <span className='Gray'>{histData.low}</span>{' '}
      {firstSign} {wantedPrice} {secondSign}{' '}
      <span className='Gray'>{histData.high}</span>
    </p>);
  }

  const detailedHistoricDataM = useMemo(DetailedHistoricData,
    [wantedHistDataIndex, allHistPriceData, summaryM]);
  function DetailedHistoricData() {
    if (allHistPriceData === undefined) {
      return (<div>Loading...</div>);
    } else if (!allHistPriceData && isSessionGeoFenced) {
      return geoFencedNotice;
    } else if (!allHistPriceData) {
      return (<div>
        Failed to fetch historic data. Try again later or ask for help at our{' '}
        {telegramGroupHyperlink} for help
      </div>);
    }
    const historicData = allHistPriceData[wantedHistDataIndex];
    return (<React.Fragment>
      <div className='Grid'>
        <TableItem className='open'
          header='Opening price' content={historicData.open}
        />
        <TableItem className='openTime'
          header='Observed at' content={historicData.openTime}
        />
        <TableItem className='low'
          header='Lowest price' content={historicData.low}
        />
        <TableItem className='high'
          header='Highest price' content={historicData.high}
        />
        <TableItem className='close'
          header='Closing price' content={historicData.close}
        />
        <TableItem className='closeTime'
          header='Observed at' content={historicData.closeTime}
        />
      </div>

      <ControlButtons />
      {summaryM}
    </React.Fragment>);
  }

  function ControlButtons() {
    return (<div className='ControlButtons'>
      <button className='Advanced' onClick={UsePreviousSecondOfPriceData}>
        previous second
      </button>
      <button className='Advanced' onClick={UseNextSecondOfPriceData}>
        next second
      </button>
    </div>);
  }


  return (<div className='HistoricData' id={props.id || null}>
    {introductionM}
    {detailedHistoricDataM}
    <div className='SourceURL'>
      <p>Source data URL:</p>
      <a target='_blank' rel='noreferrer noopener' href={feedURL}>{feedURL}</a>
    </div>
  </div>);
}


export default HistoricPriceFeedData;
